import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  School, GraduationCap, Users, BookOpen,
  ArrowRight, CheckCircle, Globe, Shield, Cpu,
  Building2, ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  HeroText,
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  InteractiveCard,
  MagneticButton,
  FloatingElement,
} from '@/components/ui/OptimizedAnimations';
import {
  CharacterStagger,
  SparklesCore,
  Spotlight,
} from '@/components/ui/AceternityAnimations';

const products = [
  {
    id: 'school-erp',
    name: 'School ERP',
    tagline: 'Complete K-12 Management',
    description: 'End-to-end school management system with AI-powered analytics, smart attendance, fee management, and parent engagement tools.',
    icon: School,
    color: 'teal',
    features: ['Admission Management', 'Fee & Finance', 'Smart Attendance', 'Parent Portal', 'Timetable', 'Transport'],
    audience: 'K-12 Schools',
    cta: 'Book a Demo',
  },
  {
    id: 'college-erp',
    name: 'College ERP',
    tagline: 'Higher Education Suite',
    description: 'Comprehensive university management platform with NEP 2020 compliance, research tracking, and placement automation.',
    icon: GraduationCap,
    color: 'orange',
    features: ['NEP 2020 Compliance', 'Research Portal', 'Placement Module', 'Alumni Network', 'Faculty Portal', 'Library System'],
    audience: 'Universities & Colleges',
    cta: 'Book a Demo',
  },
  {
    id: 'lynkt',
    name: 'Lynkt',
    tagline: 'Teacher Empowerment Platform',
    description: 'Professional development platform with certified courses, peer networking, and career growth opportunities for educators.',
    icon: Users,
    color: 'teal',
    features: ['Certified Courses', 'Peer Networking', 'Career Growth', 'Content Creation', 'Free Tools', 'Community'],
    audience: 'Individual Teachers',
    cta: 'Start Free Trial',
  },
  {
    id: 'lms',
    name: 'LMS',
    tagline: 'Learning Management System',
    description: 'AI-powered learning platform with personalized learning paths, assessments, and real-time progress tracking.',
    icon: BookOpen,
    color: 'orange',
    features: ['AI Tutoring', 'Video Library', 'Assessments', 'Progress Tracking', 'Gamification', 'Analytics'],
    audience: 'Institutions & Corporates',
    cta: 'Book a Demo',
  },
];

const globalStats = [
  { value: '500+', label: 'Institutions Worldwide' },
  { value: '15+', label: 'Countries' },
  { value: '1M+', label: 'Users' },
  { value: '99.9%', label: 'Uptime' },
];

const trustBadges = [
  { icon: Globe, label: 'Global Presence', desc: 'US, Australia, India' },
  { icon: Shield, label: 'ISO 27001', desc: 'Certified Security' },
  { icon: Cpu, label: 'AI-Powered', desc: 'Smart Automation' },
];

const ProductCard = ({ product, index }) => {
  const isEven = index % 2 === 0;

  return (
    <ScrollReveal delay={index * 0.1}>
      <InteractiveCard
        hoverScale={1.02}
        hoverY={-8}
        glowColor={product.color === 'teal' ? 'rgba(0,191,165,0.3)' : 'rgba(255,107,53,0.3)'}
      >
        <div className={cn(
          'glass-card p-8 h-full',
          'border transition-colors duration-300',
          product.color === 'teal' ? 'hover:border-teal/40' : 'hover:border-orange/40'
        )}>
          <div className="flex flex-col h-full">
            <div className="flex items-start justify-between mb-6">
              <div className={cn(
                'w-16 h-16 rounded-2xl flex items-center justify-center',
                product.color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
              )}>
                <product.icon className={cn(
                  'w-8 h-8',
                  product.color === 'teal' ? 'text-teal' : 'text-orange'
                )} />
              </div>
              <span className={cn(
                'px-3 py-1 rounded-full text-xs font-mono',
                product.color === 'teal' ? 'bg-teal/10 text-teal' : 'bg-orange/10 text-orange'
              )}>
                {product.audience}
              </span>
            </div>

            <h3 className="text-display text-2xl text-foreground mb-2">
              {product.name}
            </h3>
            <p className={cn(
              'text-sm font-medium mb-4',
              product.color === 'teal' ? 'text-teal' : 'text-orange'
            )}>
              {product.tagline}
            </p>
            <p className="text-muted-foreground text-sm mb-6 flex-grow">
              {product.description}
            </p>

            <div className="grid grid-cols-2 gap-2 mb-6">
              {product.features.map((feature) => (
                <div key={feature} className="flex items-center gap-2">
                  <CheckCircle className={cn(
                    'w-4 h-4 flex-shrink-0',
                    product.color === 'teal' ? 'text-teal' : 'text-orange'
                  )} />
                  <span className="text-xs text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-4 mt-auto">
              <Link to={`/products/${product.id}`} className="flex-1">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    'w-full py-3 px-6 rounded-full font-medium transition-all flex items-center justify-center gap-2',
                    product.color === 'teal'
                      ? 'bg-teal text-primary-foreground hover:bg-teal-light shadow-teal-glow'
                      : 'bg-orange text-primary-foreground hover:bg-orange-light shadow-orange-glow'
                  )}
                >
                  {product.cta}
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
              <Link to={`/products/${product.id}`}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="p-3 rounded-full border border-glass-border hover:border-muted-foreground transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </InteractiveCard>
    </ScrollReveal>
  );
};

const ProductsPage = () => {
  return (
    <div className="relative bg-void min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="hsl(166, 100%, 37%)" />

        <div className="absolute inset-0 z-[1]">
          <SparklesCore
            id="productsSparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={30}
            particleColor="#00BFA5"
          />
        </div>

        <FloatingElement duration={8} distance={20} className="absolute top-20 right-10 opacity-10">
          <div className="w-64 h-64 border border-teal/30 rounded-full" />
        </FloatingElement>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <ScrollReveal delay={0.1}>
              <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
                Global Education ERP Solutions
              </span>
            </ScrollReveal>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-foreground mb-4">
              <CharacterStagger
                text="The Complete Education"
                delay={0.2}
                staggerDelay={0.02}
              />
            </h1>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-gradient-teal mb-6">
              <CharacterStagger
                text="Technology Suite"
                delay={0.6}
                staggerDelay={0.02}
              />
            </h1>

            <ScrollReveal delay={0.8}>
              <p className="text-muted-foreground text-lg sm:text-xl max-w-2xl mx-auto mb-8">
                Trusted by institutions across the US, Australia, and India.
                Four powerful products designed to transform every aspect of education management.
              </p>
            </ScrollReveal>

            {/* Trust Badges */}
            <ScrollReveal delay={1}>
              <div className="flex flex-wrap justify-center gap-6 mb-12">
                {trustBadges.map((badge) => (
                  <div
                    key={badge.label}
                    className="flex items-center gap-3 px-4 py-2 rounded-full bg-glass-surface border border-glass-border"
                  >
                    <badge.icon className="w-5 h-5 text-teal" />
                    <div className="text-left">
                      <p className="text-xs font-medium text-foreground">{badge.label}</p>
                      <p className="text-xs text-muted-foreground">{badge.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 border-y border-glass-border bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {globalStats.map((stat, index) => (
              <ScrollReveal key={stat.label} delay={index * 0.1}>
                <div className="text-center">
                  <div className="text-3xl font-display font-bold text-teal mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              Choose Your <span className="text-gradient-teal">Solution</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each product is designed to address specific needs in the education ecosystem.
              Select the one that fits your institution or explore our integrated suite.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal direction="left">
              <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
                Seamless Integration
              </span>
              <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
                All Four Products,<br />
                <span className="text-gradient-orange">One Unified Platform</span>
              </h2>
              <p className="text-muted-foreground mb-8">
                While each product works standalone, the real magic happens when they work together.
                School ERP data flows into LMS. Lynkt-trained teachers enhance the entire ecosystem.
                College ERP placement modules connect with industry partners.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Single sign-on across all products',
                  'Unified analytics dashboard',
                  'Seamless data synchronization',
                  'Centralized administration',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-orange flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <MagneticButton strength={0.2}>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-primary"
                  >
                    Discuss Enterprise Integration
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </Link>
              </MagneticButton>
            </ScrollReveal>

            <ScrollReveal direction="right">
              <div className="relative">
                {/* Integration Diagram */}
                <div className="grid grid-cols-2 gap-4">
                  {products.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className={cn(
                        'glass-card p-6 text-center',
                        'border transition-all duration-300',
                        product.color === 'teal' ? 'hover:border-teal/40' : 'hover:border-orange/40'
                      )}
                    >
                      <div className={cn(
                        'w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3',
                        product.color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
                      )}>
                        <product.icon className={cn(
                          'w-6 h-6',
                          product.color === 'teal' ? 'text-teal' : 'text-orange'
                        )} />
                      </div>
                      <h4 className="text-sm font-display font-semibold text-foreground">
                        {product.name}
                      </h4>
                    </motion.div>
                  ))}
                </div>
                {/* Connection Lines (visual only) */}
                <div className="absolute inset-0 pointer-events-none">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <line x1="25" y1="25" x2="75" y2="25" stroke="rgba(0,191,165,0.3)" strokeWidth="0.5" strokeDasharray="2,2" />
                    <line x1="25" y1="75" x2="75" y2="75" stroke="rgba(255,107,53,0.3)" strokeWidth="0.5" strokeDasharray="2,2" />
                    <line x1="25" y1="25" x2="25" y2="75" stroke="rgba(0,191,165,0.3)" strokeWidth="0.5" strokeDasharray="2,2" />
                    <line x1="75" y1="25" x2="75" y2="75" stroke="rgba(255,107,53,0.3)" strokeWidth="0.5" strokeDasharray="2,2" />
                    <line x1="25" y1="25" x2="75" y2="75" stroke="rgba(0,191,165,0.2)" strokeWidth="0.5" strokeDasharray="2,2" />
                    <line x1="75" y1="25" x2="25" y2="75" stroke="rgba(255,107,53,0.2)" strokeWidth="0.5" strokeDasharray="2,2" />
                  </svg>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-void">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
              Not Sure Which Product<br />
              <span className="text-gradient-teal">Fits Your Needs?</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Our education technology consultants can help you identify the right solution
              for your institution. Schedule a free consultation to discuss your requirements.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <MagneticButton strength={0.2}>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-primary px-8 py-4"
                  >
                    Schedule Consultation
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.2}>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-secondary px-8 py-4"
                  >
                    Compare Products
                  </motion.button>
                </Link>
              </MagneticButton>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
